// Percorso file: src/pages/MyCalendar.jsx
//
// CALENDAR DEL SOCIO — doc 07 §13 («Calendar — pagina del socio»).
//
// ⚠️ LA COSTRUZIONE FU VESTE, NON LOGICA: la pagina prendeva GLI STESSI
// DATI e LA STESSA LOGICA del ramo «My Week» di components/CalendarClub.jsx
// e li rivestiva secondo il §13. I due casi che quella fetta si riservava
// di guardare al collaudo — il coach con più squadre e il genitore in
// delega — sono stati poi guardati davvero, ed è arrivato IL RITOCCO che
// allora si annunciava: **una squadra sola per ciascuno**, che la pagina
// RICEVE invece di dedurla. Sono le due rotte con parametro qui sotto.
// Il §13 non è stato ridisegnato: la veste è intatta, cambia QUALI dati
// entrano nel blocco del contesto, non come si mostrano.
//
// ⚠️ `CalendarClub.jsx` NON È STATO TOCCATO, ed è voluto: resta la pagina
// dell'ADMIN (tabella di club, «Select teams», Weekly Planner, «+ Add
// Event»), che il §13 dichiara «per ora resta com'è». Le due pagine
// COESISTONO su rotte diverse — /calendar (com'era) e /my-calendar
// (questa) — ed è uno stato TRANSITORIO voluto dal committente.
//
// ⚠️ LE TRE LETTURE DI SQUADRA NON SONO PIÙ COPIE (cantiere «lettore
// unico», fetta F4). `squadreDelCoach`, `squadraDelGiocatore` e
// `eDiUnaMiaSquadra` vivevano qui come copie dichiarate di CalendarClub.jsx,
// in attesa — parole del commento che stava qui — che «le due copie
// vengano riunite in un modulo solo». Il modulo esiste, è
// `utils/squadre.js`, e le due pagine lo importano tutte e due.
// ⚠️ Gli aiutanti di data `dataEvento`, `getWeekNumber` e `getWeekRange`
// vengono dalla sede utils/calendario.js (giro G-K1). Resta locale solo
// `evTimeStamp`: diversa da quella della sede (una parentesi) e con
// algoritmo diverso da quella di CalendarClub.jsx.
//
// SOLA LETTURA. Il socio qui non scrive: nessun «+ Add Event», nessuna
// modale evento, nessuna matita, nessun cestino, nessun «Export CSV».
// Quei comandi sono e restano degli amministratori (fetta B del cantiere:
// il backend rifiuta POST/PUT/DELETE su /events a chi non è ADMIN).

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FaChevronLeft, FaChevronRight, FaCalendarAlt } from "react-icons/fa";
import HomeShell from "../components/HomeShell";
import { PastigliaCategoriaSeNota } from "../components/PastigliaCategoria";
import { useAuth } from "../context/AuthContext";
import { api } from "../api/api";
import { isActingAs, normalizza } from "../utils/roleUtils";
// LA SEDE UNICA DELLA LETTURA DI SQUADRA (fetta F4): le tre letture che
// questa pagina teneva in copia locale. `canonicalRole` non si importa più:
// lo applica il modulo, dentro il filtro di ruolo. `normalizza` resta,
// perché la pagina lo usa anche per appaiare la CATEGORIA della squadra.
// ⚠️ `primaSquadraDellaTessera` NON si importa più: il ramo del giocatore non
// legge la sola tessera indossata (v. D7 qui sotto). La primitiva resta viva
// nel lettore unico ed è ancora chiamata da CalendarClub.jsx.
import { eDiUnaMiaSquadra, squadreDelSoggetto } from "../utils/squadre";
import { dataEvento, getWeekNumber, getWeekRange } from "../utils/calendario";
import "./MyCalendar.css";

// ===========================================================================
// AIUTANTI DI DATA — `getWeekRange`, `getWeekNumber` e `dataEvento` dalla sede
// unica utils/calendario.js (giro G-K1). `dataEvento` legge i DUE formati che i
// dati portano davvero: la My Week storica stampava con `new Date(ev.date)`
// diretto, che sul formato `DD-MM-YYYY` scriveva a schermo «INVALID DATE».
// ===========================================================================

/** Istante dell'evento (data + ora), per l'ordinamento.
 *  ⚠️ Resta qui: NON è a token la copia di utils/calendario.js (parentesi in
 *  più attorno alla condizione del ternario). Giro G-K1: si lascia com'è. */
function evTimeStamp(ev) {
  const d = dataEvento(ev);
  if (!d) return 0;

  const t = (ev.time && /^\d{2}:\d{2}$/.test(ev.time)) ? ev.time : "00:00";
  const [hh, mm] = t.split(":").map(Number);
  d.setHours(hh, mm, 0, 0);
  return d.getTime();
}

// ===========================================================================
// LE MIE SQUADRE — le copie di pagina NON CI SONO PIÙ (fetta F4)
// ===========================================================================
// Qui vivevano `squadreDelCoach`, `squadraDelGiocatore` e `eDiUnaMiaSquadra`,
// copie dichiarate di CalendarClub.jsx. Il commento che stava qui prometteva:
// «quando la pagina admin sarà rifatta, le due copie vanno riunite in un
// modulo solo». Il modulo esiste — `utils/squadre.js`, cantiere «lettore
// unico» — e le due pagine ora lo IMPORTANO tutte e due: i corpi sono quelli
// di qui, invariati, e le copie sono sei in meno fra le due pagine.
//
// ⚠️ RESTA VERO CIÒ CHE QUELLE COPIE DICEVANO: la sorgente è la coppia
// `teams` / `allTeams` della TESSERA, e i due campi si guardano SEMPRE
// INSIEME. NON si leggono `activeContext.team` (derivato: su una tessera
// COACH porta la spia di ruolo "TEAMTD", che non è il nome di nessuna
// squadra), `user.team` e `user.teams` (campi morti, nessuna rotta li
// scrive).
//
// LE TRE CHIAMATE sono quelle di CalendarClub.jsx, con le stesse tre
// differenze VOLUTE — e il blocco gemello in testa a quel file le spiega per
// esteso, riga per riga:
//   D6  `ruoli: ["COACH"]` — le sole tessere COACH per le squadre ALLENATE;
//   D7  il giocatore ha UNA squadra sola, e `allTeams` NON si legge («tutte
//       le squadre» è un permesso da allenatore: su una tessera PLAYER
//       nessun lettore lo onora, e il ramo lo azzera esplicitamente);
//   D8  `anno = null` — il calendario non conosce le stagioni: `user.contexts`
//       arriva GIÀ filtrato all'anno operativo da AuthContext
//       (`tessereDellAnnoOperativo`), e rifiltrare qui sarebbe un secondo
//       cancello sullo stesso dato.
//
// ⚠️ D7 e D8 SONO CAMBIATI NEL RAMO DEL GIOCATORE, e per una ragione sola:
// LA HOME E IL CALENDARIO DEVONO DIRE LA STESSA SQUADRA. Il ramo player
// leggeva `primaSquadraDellaTessera(activeContext)`, cioè la SOLA tessera
// indossata, mentre `HomePlayer.jsx` legge l'UNIONE delle tessere con
// `squadreDelSoggetto(user?.contexts, annoOperativo)`: due letture diverse
// dello stesso fatto, e bastava che la tessera con la squadra non fosse in
// posizione 0 perché la home scrivesse il nome e il calendario no. Il ramo
// player usa ora la riga della home — con l'ANNO OPERATIVO, quindi D8 NON
// vale più lì. Il secondo cancello sull'anno c'è, ed è VOLUTO: è lo stesso
// che la home applica, e applicarne uno diverso significherebbe tornare a
// due risposte per una domanda sola. D8 resta invece intatto nel ramo COACH,
// che continua a passare `null` come `HomeCoach.jsx`.
//
// ⚠️ LE DUE ROTTE CON PARAMETRO (`/my-calendar/team/:teamName`,
// `/my-calendar/child/:childId`) non sono un quarto lettore di squadra: la
// squadra dell'allenatore gliel'ha già scelta la SUA home, e quella del
// figlio esce dalle tessere del FIGLIO con la stessa riga di
// `HomeChild.jsx`. Nessuna nuova regola, solo il dato che viaggia.
//
// ⚠️ CAMBIA UN ESITO (fetta F4, Q5.1/Q5.2): le copie tolte leggevano
// `ctx.teams` / `ctx.allTeams` CRUDI, e la sorgente è grezza — `GET
// /users?uid=` restituisce i `contexts` così come stanno sul documento
// (`server.js`). Il modulo porta il ripiego della «regola B»,
// quindi un coach o un giocatore con tessera di forma vecchia — squadre nel
// solo `approvedTeams` — vede da qui in avanti la sua settimana PIENA invece
// che vuota. Chi ha la tessera nuova non vede alcuna differenza.

/**
 * IL TIPO DELL'EVENTO, col metro della My Week storica: è «training» solo
 * ciò che il dato chiama esattamente `"Training"`; tutto il resto (Game,
 * Bye, tipo assente) è trattato come partita. Non è una classificazione
 * nuova — è la stessa espressione di CalendarClub.jsx, che sceglie
 * `training-right` oppure `game-left`.
 */
const eAllenamento = (ev) => ev && ev.type === "Training";

export default function MyCalendar() {
  const navigate = useNavigate();
  const { user, activeContext, operatingSeason } = useAuth() || {};

  // L'anno operativo è del CLUB, non della persona: si prende dalla
  // cassettina di AuthContext, con la stessa riga di HomePlayer, HomeChild,
  // HomeParent e UsersList. Fuori da `seasonStatus: "ready"` vale `null`, e
  // il predicato dell'anno lo tratta come «annata ignota».
  const annoOperativo = operatingSeason == null ? null : operatingSeason;

  // ---------------------------------------------------------------------
  // DI CHI È LA SETTIMANA — i due parametri di rotta.
  //
  // La pagina serve tre indirizzi, e sono TRE perché una squadra e un id
  // non si distinguono a occhio:
  //   /my-calendar                    l'utente collegato, com'era;
  //   /my-calendar/team/:teamName     la squadra che l'allenatore ha aperto
  //                                   nella sua home (`squadraScelta`, che
  //                                   è stato interno di HomeCoach e senza
  //                                   questo parametro non uscirebbe di lì);
  //   /my-calendar/child/:childId     il figlio che il genitore sta
  //                                   guardando, con lo STESSO parametro di
  //                                   rotta di `/home/child/:childId`.
  //
  // ⚠️ IL PARAMETRO, NON LO STATO DI NAVIGAZIONE. `navigate(to, { state })`
  // non sopravvive al ricaricamento della pagina e nel frontend non ha
  // nemmeno uno scrittore vivo: l'unica strada in casa è il parametro, ed è
  // quella che il genitore percorre già per aprire la home del figlio.
  // ---------------------------------------------------------------------
  const { teamName, childId } = useParams();
  const squadraDallaRotta = String(teamName || "").trim();
  const idFiglio = String(childId || "").trim();

  // ---------------------------------------------------------------------
  // IL CAPPELLO — «che cosa sta guardando adesso», mai «che cosa può fare».
  // Da qui dipende SOLO ciò che si vede. Il metro è il gate unico
  // (auth/access.js via roleUtils), tollerante alle grafie storiche:
  // "Player"/"PLAYER" si equivalgono, "Manager"/"TD"/"ManagerTD" valgono
  // COACH, "Child" vale PLAYER.
  // ---------------------------------------------------------------------
  const indossaPlayer = isActingAs(user, activeContext, "PLAYER");
  const indossaCoach = isActingAs(user, activeContext, "COACH");

  // ---------------------------------------------------------------------
  // LA SCHEDA DEL FIGLIO — solo se la rotta ne porta l'id.
  //
  // È la stessa lettura di HomeChild.jsx, riga per riga, e per la stessa
  // ragione: la squadra sta nelle TESSERE del figlio, e le tessere stanno
  // sul suo documento.
  //
  // ⚠️ IL PERMESSO NON SI VALUTA QUI, e non potrebbe: si chiede il dato
  // dove il dato sta, e `GET /users/:id` (server.js) risponde 403 a
  // chi non è tutore del soggetto — né amministratore, né allenatore di una
  // sua squadra. Un rifiuto e un guasto di rete finiscono nello STESSO
  // ramo, di proposito: distinguerli a schermo direbbe a un estraneo se
  // quell'id esiste.
  //
  // ⚠️ IL RIPIEGO È MUTO. A `stato !== "ok"` il cappello resta vuoto MA
  // filtrante (v. `cappelloDiSquadra` più sotto): niente nome di squadra,
  // settimana vuota, nessun messaggio. Non si scrive «not available» e non
  // si mostra la settimana del club al posto di quella del figlio — la
  // prima direbbe qualcosa del minore, la seconda fingerebbe una risposta.
  // Vale anche durante l'attesa, così non c'è nemmeno il lampeggio.
  // ---------------------------------------------------------------------
  const [figlio, setFiglio] = useState(null);
  const [statoFiglio, setStatoFiglio] = useState("assente");

  useEffect(() => {
    if (!idFiglio) {
      setFiglio(null);
      setStatoFiglio("assente");
      return undefined;
    }

    let annullato = false;

    setStatoFiglio("caricamento");
    setFiglio(null);

    api
      .get(`/users/${encodeURIComponent(idFiglio)}`)
      .then((documento) => {
        if (annullato) return;

        if (!documento || typeof documento !== "object") {
          setStatoFiglio("errore");
          return;
        }

        setFiglio(documento);
        setStatoFiglio("ok");
      })
      .catch(() => {
        if (!annullato) {
          setFiglio(null);
          setStatoFiglio("errore");
        }
      });

    return () => {
      annullato = true;
    };
  }, [idFiglio]);

  // LA SQUADRA CHE SI STA GUARDANDO — §13.2. UNA SOLA PER CIASCUNO.
  //
  // I rami sono quattro, e l'ordine è parte della regola: chi si guarda
  // viene PRIMA di che cappello si porta, perché il genitore che apre il
  // figlio non smette di essere un genitore e la sua squadra non c'entra.
  //
  //   FIGLIO (rotta)  → la squadra del FIGLIO, dalle sue tessere, con la
  //                     stessa riga di HomeChild.jsx. A documento non
  //                     letto — negato, guasto, o ancora in arrivo —
  //                     l'elenco è vuoto e la settimana esce vuota.
  //   SQUADRA (rotta) → la squadra che l'allenatore ha aperto nella sua
  //                     home: UNA, quella, e non l'unione delle sue. Vale
  //                     anche per il coach «tutte le squadre», il cui
  //                     elenco di tessere non ha nomi propri: il nome che
  //                     si mostra è quello ricevuto, che nel suo caso esce
  //                     dall'elenco del CLUB (HomeCoach.jsx). Nessun
  //                     nome inventato: si mostra ciò che è stato scelto.
  //   COACH           → RIPIEGO per chi arriva senza parametro (indirizzo
  //                     digitato, segnalibro, ritorno del browser): le
  //                     squadre delle sue tessere COACH, cioè il
  //                     comportamento di prima. È l'unico ramo che può
  //                     ancora dire PIÙ nomi, e solo perché in quel caso
  //                     una squadra scelta non c'è.
  //   PLAYER          → la sua squadra, con la riga della SUA home
  //                     (HomePlayer.jsx): unione delle tessere
  //                     dell'anno operativo, filtrata alle sole tessere
  //                     PLAYER, e se ne prende UNA — l'invariante del club
  //                     è «un giocatore, una squadra». Se le tessere ne
  //                     dessero più d'una si mostra la prima, la stessa
  //                     che mostra la home: non si inventa una scelta, e
  //                     il caso è a verbale come anomalia di DATI.
  //                     `tutte` si azzera (D7): «tutte le squadre» è un
  //                     permesso da allenatore, e un giocatore che avesse
  //                     una tessera così non deve vedere il club intero.
  //
  // Chi non è nessuno di questi — un genitore che apra `/my-calendar` nudo,
  // un socio senza cappello di squadra — resta nel ramo neutro: elenco
  // vuoto e NESSUN filtro, cioè la settimana del club, esattamente come
  // oggi. Nessun dato altrui viene esposto: gli eventi del club sono già
  // leggibili da ogni socio collegato, qui come su /outlook.
  const cappello = useMemo(() => {
    if (idFiglio) {
      if (statoFiglio !== "ok") return { nomi: [], tutte: false };

      const { nomi, tutte } = squadreDelSoggetto(figlio?.contexts, annoOperativo);
      const squadra = tutte ? "" : nomi[0] || "";

      return { nomi: [squadra].filter(Boolean), tutte: false };
    }

    if (squadraDallaRotta) {
      return { nomi: [squadraDallaRotta], tutte: false };
    }

    if (indossaCoach) {
      return squadreDelSoggetto(user?.contexts, null, { ruoli: ["COACH"], doppioni: "normalizzati" });
    }

    if (indossaPlayer) {
      const { nomi, tutte } = squadreDelSoggetto(user?.contexts, annoOperativo, {
        ruoli: ["PLAYER"]
      });
      const squadra = tutte ? "" : nomi[0] || "";

      return { nomi: [squadra].filter(Boolean), tutte: false };
    }

    return { nomi: [], tutte: false };
  }, [
    user,
    annoOperativo,
    idFiglio,
    figlio,
    statoFiglio,
    squadraDallaRotta,
    indossaCoach,
    indossaPlayer
  ]);

  // Solo un cappello DI SQUADRA filtra (stessa condizione di CalendarClub),
  // e i due parametri di rotta ne sono uno: chi entra da una squadra o da un
  // figlio sta guardando QUELLA settimana, non quella del club.
  //
  // ⚠️ È QUI CHE IL RIPIEGO DEL FIGLIO DIVENTA MUTO: con `idFiglio` il
  // filtro scatta SEMPRE, anche a elenco vuoto — documento negato o non
  // ancora arrivato — e la settimana esce vuota invece che piena. Togliere
  // `idFiglio` da questa riga mostrerebbe la settimana intera del club a chi
  // sul minore non ha alcun diritto, ed è esattamente il guasto che questa
  // fetta chiude.
  const cappelloDiSquadra =
    indossaPlayer || indossaCoach || Boolean(squadraDallaRotta) || Boolean(idFiglio);

  // Chiave STRINGA dell'elenco: serve come dipendenza dell'effetto al posto
  // dell'array, che è un oggetto nuovo a ogni giro. Stessa cautela di
  // HomePlayer.jsx e HomeParent.jsx.
  const chiaveSquadre = cappello.nomi.join("|");

  // ---------------------------------------------------------------------
  // EVENTI — `GET /events` senza parametri, filtro nel browser: è la sola
  // lettura che il calendario ha sempre fatto. La rotta è in lettura per
  // ogni socio collegato (fetta A).
  // ---------------------------------------------------------------------
  const [events, setEvents] = useState([]);

  const loadEvents = useCallback(async () => {
    try {
      const data = await api.get("/events");
      setEvents(Array.isArray(data) ? data : []);
    } catch (e) {
      console.error("Failed to load events:", e);
      setEvents([]);
    }
  }, []);

  // ---------------------------------------------------------------------
  // SETTIMANA — stesso navigatore della pagina storica: si sposta di sette
  // giorni per volta e si parte da oggi, a mezzanotte.
  // ---------------------------------------------------------------------
  const [currentWeek, setCurrentWeek] = useState(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  });

  useEffect(() => { loadEvents(); }, [currentWeek, loadEvents]);

  function prevWeek() {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() - 7);
    setCurrentWeek(d);
  }
  function nextWeek() {
    const d = new Date(currentWeek);
    d.setDate(d.getDate() + 7);
    setCurrentWeek(d);
  }

  const [weekStartStr, weekEndStr, weekStart, weekEnd] = getWeekRange(currentWeek);
  const weekNum = getWeekNumber(currentWeek);

  // Filtro della settimana, locale-safe: la data dell'evento si confronta
  // coi due estremi, tutti e tre azzerati all'ora zero.
  const nellaSettimana = (ev) => {
    const d = dataEvento(ev);
    if (!d) return false;
    d.setHours(0, 0, 0, 0);
    return d >= weekStart && d <= weekEnd;
  };

  const settimana = events
    .filter(nellaSettimana)
    .sort((a, b) => evTimeStamp(a) - evTimeStamp(b));

  // ⚠️ IL VALORE INIZIALE È «TUTTO», e resta tale SOLO per chi non porta un
  // cappello di squadra. Per PLAYER e COACH il filtro scatta sempre: con
  // l'elenco vuoto — allenatore senza squadre assegnate — la settimana
  // esce VUOTA, che è la risposta vera, e non «tutto il club». L'unica
  // strada che porta «tutto» dentro la propria settimana è `allTeams`.
  let miaSettimana = settimana;

  if (cappelloDiSquadra && !cappello.tutte) {
    miaSettimana = settimana.filter((ev) => eDiUnaMiaSquadra(ev.team, cappello.nomi));
  }

  // ---------------------------------------------------------------------
  // CATEGORIA DELLA SQUADRA (§2) — l'unica chiamata in più della pagina, e
  // serve a una cosa sola: la pastiglia. La categoria non sta sull'utente
  // ma sulla SQUADRA, e la tessera porta il NOME della squadra, non il suo
  // id. Se `GET /teams` fallisce non si avvisa e non si riprova: la
  // pastiglia tace e il nome della squadra resta a schermo. Stessa logica
  // di HomePlayer.jsx, HomeParent.jsx e HomeChild.jsx.
  // ---------------------------------------------------------------------
  const [squadreClub, setSquadreClub] = useState([]);

  useEffect(() => {
    if (!chiaveSquadre) return undefined;

    let annullato = false;

    api
      .get("/teams")
      .then((dati) => {
        if (!annullato) setSquadreClub(Array.isArray(dati) ? dati : []);
      })
      .catch(() => {
        /* nessuna categoria: la pastiglia tace */
      });

    return () => {
      annullato = true;
    };
  }, [chiaveSquadre]);

  // Ricerca per NOME, col metro di casa sui due lati: è lo stesso confronto
  // che `eDiUnaMiaSquadra` applica agli eventi.
  const categoriaDi = (nome) => {
    const voce = squadreClub.find(
      (t) => t && typeof t === "object" && normalizza(t.name) === normalizza(nome)
    );
    return voce ? normalizza(voce.category) : "";
  };

  return (
    // §13.1 TELAIO CONDIVISO: barra utente §7.1 + banda §7.2 col SOLO
    // «‹ Back». Niente «Switch role» — il §13.1 chiede una banda a una voce
    // — e niente barra di comandi propria: la vecchia «❌ Exit / ⏪ Back»
    // rossa non esiste più, il ritorno è il Back della banda.
    //
    // Il «Back» è una FUNZIONE e non una rotta, perché a questa pagina si
    // arriva da più parti (home del giocatore, home del coach dentro una
    // squadra, home del figlio): una destinazione fissa sarebbe sbagliata
    // per almeno una di esse. È la stessa scelta della scheda del
    // tesserato (NavBand accetta una funzione, doc 07 §11.1).
    <HomeShell backTo={() => navigate(-1)} showSwitch={false}>
      {/* §13.3.2 — titolo di pagina: la classe CONDIVISA del telaio
          (XXL, Rosso QPSC, centrata). Testo NEUTRO: la pagina serve anche
          il genitore che guarda il figlio, per cui «My Calendar» sarebbe
          impreciso. */}
      <h1 className="qpsc-page-title">Calendar</h1>

      {/* §13.3.3 — CONTESTO: la squadra che si sta guardando, col nome in
          oro chiaro e la pastiglia categoria (§2) a fianco, IN CIMA e non
          ripetuta a ogni riga. La pastiglia TACE se la categoria non è
          nota: nessun timbro «NO CATEGORY» addosso a una squadra.
          Il blocco non si disegna se non c'è nessun nome da dire — chi non
          porta un cappello di squadra, o il figlio il cui documento non è
          stato letto: meglio niente che un'etichetta inventata.
          ⚠️ IL NOME È UNO SOLO in tutti i casi che portano un parametro di
          rotta, e nel ramo del giocatore; l'elenco resta capace di più
          righe per il solo RIPIEGO dell'allenatore che apra la pagina senza
          aver scelto una squadra. La resa non cambia: è la stessa mappa. */}
      {cappello.nomi.length > 0 && (
        <div className="qpsc-cal-context">
          {cappello.nomi.map((nome) => (
            <div className="qpsc-cal-context__row" key={nome}>
              <span className="qpsc-cal-context__team">{nome}</span>
              <PastigliaCategoriaSeNota category={categoriaDi(nome)} />
            </div>
          ))}
        </div>
      )}

      {/* §13.3.4 — NAVIGATORE A SETTIMANA: frecce Font Awesome (§5: mai
          glifi unicode), intervallo di date in oro chiaro, «Week N» col
          numero in Rosso QPSC. */}
      <div className="qpsc-cal-weeknav">
        <button
          type="button"
          className="qpsc-cal-weeknav__arrow"
          onClick={prevWeek}
          aria-label="Previous week"
        >
          <FaChevronLeft />
        </button>

        <div className="qpsc-cal-weeknav__center">
          <span className="qpsc-cal-weeknav__dates">
            {weekStartStr} &mdash; {weekEndStr}
          </span>
          <span className="qpsc-cal-weeknav__week">Week {weekNum}</span>
        </div>

        <button
          type="button"
          className="qpsc-cal-weeknav__arrow"
          onClick={nextWeek}
          aria-label="Next week"
        >
          <FaChevronRight />
        </button>
      </div>

      {/* §13.3.5 — LE GIORNATE, una card §8 per evento. Le DUE DEROGHE
          §1.5 dichiarate al §13.4 vivono nel modificatore della card:
            (A) allineamento — Training a destra, Game a sinistra;
            (B) colore della pastiglia del tipo evento.
          Il tipo si distingue SOLO così: nessun altro segno, nessun
          bordo colorato, nessun corsivo. */}
      <div className="qpsc-cal-days">
        {miaSettimana.length === 0 ? (
          <p className="qpsc-cal-empty">No events scheduled this week.</p>
        ) : (
          miaSettimana.map((ev) => {
            const quando = dataEvento(ev);
            const allenamento = eAllenamento(ev);
            const tipo = allenamento ? "training" : "game";

            return (
              <article
                key={ev.id}
                className={`qpsc-cal-card qpsc-cal-card--${tipo}`}
              >
                {/* Giorno e data in oro chiaro. Se la data non si legge in
                    nessuno dei due formati non si scrive una data finta:
                    la riga tace e il resto della card resta leggibile. */}
                {quando && (
                  <span className="qpsc-cal-card__when">
                    {quando.toLocaleDateString("en-AU", { weekday: "short" }).toUpperCase()}
                    {" "}
                    {quando.toLocaleDateString("en-AU", { day: "2-digit", month: "2-digit" })}
                  </span>
                )}

                {/* DEROGA §1.5 (B): la pastiglia del tipo evento. Forma §2
                    (.qpsc-pill, classe condivisa), colore dal modificatore.
                    Non si disegna se il dato non dice il tipo: una
                    pastiglia vuota non classifica nulla. */}
                {ev.type && (
                  <span className={`qpsc-pill qpsc-cal-type--${tipo}`}>
                    {ev.type}
                  </span>
                )}

                {ev.time && <span className="qpsc-cal-card__time">{ev.time}</span>}

                {ev.location && (
                  <span className="qpsc-cal-card__place">{ev.location}</span>
                )}

                {/* Solo per le partite: l'avversario, a peso 600. */}
                {!allenamento && ev.opponent && (
                  <span className="qpsc-cal-card__opponent">vs {ev.opponent}</span>
                )}

                {/* La nota di round, in testo secondario. */}
                {ev.notes && ev.notes.trim() && (
                  <span className="qpsc-cal-card__note">{ev.notes}</span>
                )}
              </article>
            );
          })
        )}
      </div>

      {/* §13.3.6 — «Full Club Calendar»: pulsante ORO §5.1, piena colonna,
          classe CONDIVISA del telaio (nessuna copia del pulsante). Porta a
          /outlook, il calendario completo del club, in lettura per ogni
          socio collegato. */}
      <button
        type="button"
        className="qpsc-btn-gold"
        onClick={() => navigate("/outlook")}
      >
        <span className="qpsc-btn-gold__icon">
          <FaCalendarAlt />
        </span>
        Full Club Calendar
      </button>
    </HomeShell>
  );
}
